import React from 'react';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm p-8"
        >
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <Shield className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Privacy Policy</h1>
            </div>
            <Link
              to="/"
              className="flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </div>

          <div className="prose dark:prose-invert max-w-none">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Last updated: March 27, 2025</p>

            <p>
              This Privacy Policy describes Our policies and procedures on the collection,
              use and disclosure of Your information when You use the Service and tells You
              about Your privacy rights and how the law protects You.
            </p>
            
            <p>
              We use Your Personal data to provide and improve the Service. By using the
              Service, You agree to the collection and use of information in accordance with
              this Privacy Policy. This Privacy Policy has been created with the help of the
              <a href="https://www.freeprivacypolicy.com/free-privacy-policy-generator/" className="text-indigo-600 dark:text-indigo-400 hover:underline ml-1">
                Free Privacy Policy Generator
              </a>.
            </p>

            <h2>Interpretation and Definitions</h2>
            
            <h3>Interpretation</h3>
            <p>
              The words of which the initial letter is capitalized have meanings defined
              under the following conditions. The following definitions shall have the same
              meaning regardless of whether they appear in singular or in plural.
            </p>

            <h3>Definitions</h3>
            <p>For the purposes of this Privacy Policy:</p>
            <ul>
              <li><strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
              <li><strong>Affiliate</strong> means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.</li>
              <li><strong>Application</strong> refers to Serge, the software program provided by the Company.</li>
              <li><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Serge.</li>
              <li><strong>Cookies</strong> are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.</li>
              <li><strong>Country</strong> refers to: New York, United States</li>
              <li><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</li>
              <li><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
              <li><strong>Service</strong> refers to the Application or the Website or both.</li>
              <li><strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.</li>
              <li><strong>Third-party Social Media Service</strong> refers to any website or any social network website through which a User can log in or create an account to use the Service.</li>
              <li><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).</li>
              <li><strong>Website</strong> refers to Serge, accessible from <a href="https://serge.unleashsupport.com/" className="text-indigo-600 dark:text-indigo-400 hover:underline">https://serge.unleashsupport.com/</a></li>
              <li><strong>You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
            </ul>

            <h2>Collecting and Using Your Personal Data</h2>
            
            <h3>Types of Data Collected</h3>
            
            <h4>Personal Data</h4>
            <p>
              While using Our Service, We may ask You to provide Us with certain personally
              identifiable information that can be used to contact or identify You.
              Personally identifiable information may include, but is not limited to:
            </p>
            <ul>
              <li>Email address</li>
              <li>First name and last name</li>
              <li>Phone number</li>
              <li>Usage Data</li>
            </ul>

            <h4>Usage Data</h4>
            <p>
              Usage Data is collected automatically when using the Service.
            </p>
            <p>
              Usage Data may include information such as Your Device's Internet Protocol
              address (e.g. IP address), browser type, browser version, the pages of our
              Service that You visit, the time and date of Your visit, the time spent on
              those pages, unique device identifiers and other diagnostic data.
            </p>

            <h4>Information from Third-Party Social Media Services</h4>
            <p>
              The Company allows You to create an account and log in to use the Service
              through the following Third-party Social Media Services:
            </p>
            <ul>
              <li>Google</li>
              <li>Facebook</li>
              <li>Instagram</li>
              <li>Twitter</li>
              <li>LinkedIn</li>
            </ul>

            <h4>Tracking Technologies and Cookies</h4>
            <p>
              We use Cookies and similar tracking technologies to track the activity on Our
              Service and store certain information. Tracking technologies used are beacons, 
              tags, and scripts to collect and track information and to improve and analyze 
              Our Service. The technologies We use may include:
            </p>

            <ul>
              <li>
                <strong>Cookies or Browser Cookies.</strong> A cookie is a small file placed on Your
                Device. You can instruct Your browser to refuse all Cookies or to indicate
                when a Cookie is being sent. However, if You do not accept Cookies, You
                may not be able to use some parts of our Service. Unless you have adjusted
                Your browser setting so that it will refuse Cookies, our Service may use
                Cookies.
              </li>
              <li>
                <strong>Web Beacons.</strong> Certain sections of our Service and our emails may contain
                small electronic files known as web beacons (also referred to as clear
                gifs, pixel tags, and single-pixel gifs) that permit the Company, for
                example, to count users who have visited those pages or opened an email
                and for other related website statistics (for example, recording the
                popularity of a certain section and verifying system and server
                integrity).
              </li>
            </ul>

            <p>
              Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies remain on
              Your personal computer or mobile device when You go offline, while Session
              Cookies are deleted as soon as You close Your web browser. Learn more about
              cookies on the{' '}
              <a href="https://www.freeprivacypolicy.com/blog/sample-privacy-policy-template/#Use_Of_Cookies_And_Tracking" className="text-indigo-600 dark:text-indigo-400 hover:underline">
                Free Privacy Policy website
              </a>{' '}
              article.
            </p>

            <p>We use both Session and Persistent Cookies for the purposes set out below:</p>

            <ul>
              <li>
                <strong>Necessary / Essential Cookies</strong>
                <br />
                Type: Session Cookies
                <br />
                Administered by: Us
                <br />
                Purpose: These Cookies are essential to provide You with services
                available through the Website and to enable You to use some of its
                features. They help to authenticate users and prevent fraudulent use of
                user accounts. Without these Cookies, the services that You have asked for
                cannot be provided, and We only use these Cookies to provide You with
                those services.
              </li>
              <li>
                <strong>Cookies Policy / Notice Acceptance Cookies</strong>
                <br />
                Type: Persistent Cookies
                <br />
                Administered by: Us
                <br />
                Purpose: These Cookies identify if users have accepted the use of cookies
                on the Website.
              </li>
              <li>
                <strong>Functionality Cookies</strong>
                <br />
                Type: Persistent Cookies
                <br />
                Administered by: Us
                <br />
                Purpose: These Cookies allow us to remember choices You make when You use
                the Website, such as remembering your login details or language
                preference. The purpose of these Cookies is to provide You with a more
                personal experience and to avoid You having to re-enter your preferences
                every time You use the Website.
              </li>
            </ul>

            <p>
              For more information about the cookies we use and your choices regarding
              cookies, please visit our Cookies Policy or the Cookies section of our Privacy
              Policy.
            </p>

            <h3>Use of Your Personal Data</h3>
            <p>The Company may use Personal Data for the following purposes:</p>
            <ul>
              <li>To provide and maintain our Service, including to monitor the usage of our Service.</li>
              <li>To manage Your Account: to manage Your registration as a user of the Service.</li>
              <li>For the performance of a contract: the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.</li>
              <li>To contact You: To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication.</li>
              <li>To provide You with news, special offers and general information about other goods, services and events which we offer.</li>
              <li>To manage Your requests: To attend and manage Your requests to Us.</li>
              <li>For business transfers: We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which Personal Data held by Us about our Service users is among the assets transferred.</li>
              <li>For other purposes: We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our Service, products, services, marketing and your experience.</li>
            </ul>

            <p>We may share Your personal information in the following situations:</p>
            <ul>
              <li>With Service Providers: We may share Your personal information with Service Providers to monitor and analyze the use of our Service, to contact You.</li>
              <li>For business transfers: We may share or transfer Your personal information in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business to another company.</li>
              <li>With Affiliates: We may share Your information with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include Our parent company and any other subsidiaries, joint venture partners or other companies that We control or that are under common control with Us.</li>
              <li>With business partners: We may share Your information with Our business partners to offer You certain products, services or promotions.</li>
              <li>With other users: when You share personal information or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside.</li>
              <li>With Your consent: We may disclose Your personal information for any other purpose with Your consent.</li>
            </ul>

            <h3>Retention of Your Personal Data</h3>
            <p>
              The Company will retain Your Personal Data only for as long as is necessary
              for the purposes set out in this Privacy Policy. We will retain and use Your
              Personal Data to the extent necessary to comply with our legal obligations,
              resolve disputes, and enforce our legal agreements and policies.
            </p>

            <h3>Security of Your Personal Data</h3>
            <p>
              The security of Your Personal Data is important to Us, but remember that no
              method of transmission over the Internet, or method of electronic storage is
              100% secure. While We strive to use commercially acceptable means to protect
              Your Personal Data, We cannot guarantee its absolute security.
            </p>

            <h2>Children's Privacy</h2>
            <p>
              Our Service does not address anyone under the age of 13. We do not knowingly
              collect personally identifiable information from anyone under the age of 13.
              If You are a parent or guardian and You are aware that Your child has
              provided Us with Personal Data, please contact Us.
            </p>

            <h2>Links to Other Websites</h2>
            <p>
              Our Service may contain links to other websites that are not operated by Us.
              If You click on a third party link, You will be directed to that third
              party's site. We strongly advise You to review the Privacy Policy of every
              site You visit.
            </p>

            <h2>Changes to this Privacy Policy</h2>
            <p>
              We may update Our Privacy Policy from time to time. We will notify You of any
              changes by posting the new Privacy Policy on this page.
            </p>
            <p>
              We will let You know via email and/or a prominent notice on Our Service,
              prior to the change becoming effective and update the "Last updated" date at
              the top of this Privacy Policy.
            </p>

            <h2>Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, You can contact us:</p>
            <ul>
              <li>By email: inquiries@unleashsupport.com</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;